package fr.manyverse.app.infrastructure.audio

import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.error.AudioError
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import org.khronos.webgl.Uint8Array
import org.khronos.webgl.set
import org.w3c.dom.Audio
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag

private const val TAG = "AudioPlayer"

class JsAudioPlayer : AudioPlayer {
    private var audio: Audio? = null
    private var objectUrl: String? = null
    private var currentAudioData: ByteArray? = null
    private var currentSoundId: String? = null
    private var currentLoop: Boolean = true
    private var progressIntervalId: Int? = null

    // Web Audio API components for Safari-compatible volume control
    private var audioContext: AudioContext? = null
    private var gainNode: GainNode? = null
    private var sourceNode: MediaElementAudioSourceNode? = null

    private val _playbackState = MutableStateFlow(PlaybackState())
    override val playbackState: StateFlow<PlaybackState> = _playbackState.asStateFlow()

    private fun ensureAudioContext() {
        if (audioContext == null) {
            audioContext = AudioContext()
            gainNode = audioContext!!.createGain()
            gainNode!!.connect(audioContext!!.destination)
        }
    }

    override suspend fun play(audioData: ByteArray, soundId: String, loop: Boolean) {
        stop()

        currentAudioData = audioData
        currentSoundId = soundId
        currentLoop = loop

        try {
            // Initialize Web Audio API context
            ensureAudioContext()

            // Resume context (required by Safari after user interaction)
            audioContext?.resume()

            // Convert ByteArray to Blob URL
            val uint8Array = Uint8Array(audioData.size)
            for (i in audioData.indices) {
                uint8Array[i] = audioData[i]
            }
            val mimeType = when {
                audioData.size >= 2 &&
                    audioData[0] == 0xFF.toByte() &&
                    (audioData[1].toInt() and 0xF6) == 0xF0 -> "audio/aac"
                audioData.size >= 8 &&
                    audioData[4] == 0x66.toByte() && audioData[5] == 0x74.toByte() &&
                    audioData[6] == 0x79.toByte() && audioData[7] == 0x70.toByte() -> "audio/mp4"
                else -> "audio/mpeg"
            }
            val blob = Blob(arrayOf(uint8Array), BlobPropertyBag(type = mimeType))
            objectUrl = URL.createObjectURL(blob)

            audio = Audio(objectUrl!!).apply {
                this.loop = loop

                onended = {
                    if (!loop) {
                        stopProgressTracking()
                        _playbackState.value = PlaybackState(volume = _playbackState.value.volume)
                    }
                }
            }

            // Connect audio element to Web Audio API for volume control
            sourceNode = audioContext!!.createMediaElementSource(audio!!)
            sourceNode!!.connect(gainNode!!)

            // Apply current volume via GainNode
            gainNode?.gain?.value = _playbackState.value.volume

            startPlayback(audio!!)

            startProgressTracking()
        } catch (e: CancellationException) {
            // Ignore cancellation - expected when stopping or changing tracks
            throw e
        } catch (e: Exception) {
            AppLogger.error(TAG, "Lecture audio impossible", e)
            _playbackState.value = PlaybackState(volume = _playbackState.value.volume)
        }
    }

    /**
     * `play()` est asynchrone : son échec (format non supporté, lecture bloquée par le navigateur)
     * n'atteint pas le try/catch de [play] et remontait comme une promesse rejetée non gérée, en
     * laissant l'état « en lecture ».
     */
    private fun startPlayback(element: Audio) {
        element.play().catch { reason ->
            val name = reason.asDynamic().name as? String
            when (name) {
                // stop() ou un changement de piste a interrompu le démarrage : rien d'anormal
                "AbortError" -> return@catch
                // Lecture refusée tant que l'utilisateur n'a pas interagi avec la page
                "NotAllowedError" -> AppLogger.warn(TAG, "Lecture audio bloquée par le navigateur")
                else -> AppLogger.error(
                    TAG,
                    "Lecture audio refusée par le navigateur",
                    AudioError("$name: ${reason.message}")
                )
            }
            if (audio === element) {
                stopProgressTracking()
                _playbackState.value = PlaybackState(volume = _playbackState.value.volume)
            }
        }
    }

    private fun startProgressTracking() {
        stopProgressTracking()
        // Immediate first update so isPlaying is reported true right away.
        // window.setInterval only fires after the first 250ms, which left a window where
        // the player still reported isPlaying=false; in non-looping (auto-play) mode the
        // CrossfadeAudioPlayer poller misread that as "track ended" and ping-ponged tracks.
        updatePlaybackState()
        progressIntervalId = window.setInterval({
            updatePlaybackState()
        }, 250) // Update 4 times per second
    }

    private fun stopProgressTracking() {
        progressIntervalId?.let { window.clearInterval(it) }
        progressIntervalId = null
    }

    private fun updatePlaybackState() {
        val player = audio ?: return
        val duration = player.duration
        val durationMs = if (duration.isNaN()) 0L else (duration * 1000).toLong()
        _playbackState.value = PlaybackState(
            isPlaying = !player.paused,
            soundId = currentSoundId,
            volume = _playbackState.value.volume,
            isLooping = currentLoop,
            currentPositionMs = (player.currentTime * 1000).toLong(),
            durationMs = durationMs
        )
    }

    override fun pause() {
        audio?.pause()
        updatePlaybackState()
    }

    override fun resume() {
        audio?.let(::startPlayback)
        startProgressTracking()
    }

    override fun stop() {
        stopProgressTracking()
        audio?.apply {
            pause()
            currentTime = 0.0
        }
        // Note: sourceNode is not disconnected as it's automatically cleaned up
        // when the audio element is garbage collected
        sourceNode = null
        audio = null
        objectUrl?.let { URL.revokeObjectURL(it) }
        objectUrl = null
        currentAudioData = null
        currentSoundId = null
        _playbackState.value = PlaybackState(volume = _playbackState.value.volume)
    }

    override fun setVolume(volume: Float) {
        val safeVolume = volume.coerceIn(0f, 1f)
        // Use GainNode for Safari-compatible volume control
        gainNode?.gain?.value = safeVolume
        _playbackState.value = _playbackState.value.copy(volume = safeVolume)
    }

    override fun release() {
        stop()
        // Close the AudioContext to free resources
        audioContext?.close()
        audioContext = null
        gainNode = null
    }
}